import { useCallback } from 'react';
import { Group, User, RowItem, DayTotals } from '../types';

interface UseTipCalculationsProps {
  groups: Group[];
  dayTotals: DayTotals;
  dates: string[];
  tableRows: RowItem[];
}

export const useTipCalculations = ({
  groups,
  dayTotals,
  dates,
  tableRows,
}: UseTipCalculationsProps) => {
  const calculateUserTip = useCallback(
    (userId: string, date: string): number => {
      const dayTotal = dayTotals[date] || 0;

      // Find user and their group
      let user: User | undefined;
      let userGroup: Group | undefined;

      groups.forEach(g => {
        if (g.id === userId && g.users.length === 0) {
          user = { id: g.id, name: g.name, rate: g.rate, attendance: g.attendance };
          userGroup = g;
        }
        const foundUser = g.users.find(u => u.id === userId);
        if (foundUser) {
          user = foundUser;
          userGroup = g;
        }
      });

      if (!user) return 0;
      const isAttending = user.attendance[date] ?? false;
      if (!isAttending) return 0;

      // Calculate total rate of all attending entities (individuals and groups)
      const totalAttendingRate = groups.reduce((sum, g) => {
        const isGroupAttending = g.attendance[date] ?? false;
        if (!isGroupAttending) return sum;
        return sum + g.rate;
      }, 0);

      if (totalAttendingRate === 0) return 0;

      if (!userGroup || userGroup.users.length === 0) {
        // Individual user - their share based on their rate
        const individualAttending = userGroup?.attendance[date] ?? false;
        if (!individualAttending) return 0;
        return (dayTotal * (userGroup?.rate || 0)) / totalAttendingRate;
      }

      // User in a group - apply group rate first, then distribute within group
      const groupAttending = userGroup.attendance[date] ?? false;
      if (!groupAttending) return 0;

      // Group's share based on group rate
      const groupShare = (dayTotal * userGroup.rate) / totalAttendingRate;

      // Within group: distribute based on user rates
      const usersInGroupAttending = userGroup.users.filter(
        u => u.attendance[date] ?? false
      );
      const totalGroupMemberRate = usersInGroupAttending.reduce((sum, u) => sum + u.rate, 0);

      if (totalGroupMemberRate === 0) return 0;
      return (groupShare * user.rate) / totalGroupMemberRate;
    },
    [groups, dayTotals]
  );

  const getUserTotalTips = useCallback(
    (userId: string): number => {
      let total = 0;
      dates.forEach(date => {
        total += calculateUserTip(userId, date);
      });
      return total;
    },
    [dates, calculateUserTip]
  );

  const getGroupTipForDay = useCallback(
    (groupId: string, date: string): number => {
      const group = groups.find(g => g.id === groupId);
      if (!group || group.users.length === 0) return 0;

      return group.users.reduce((sum, user) => {
        return sum + calculateUserTip(user.id, date);
      }, 0);
    },
    [groups, calculateUserTip]
  );

  const getGroupTotalTips = useCallback(
    (groupId: string): number => {
      let total = 0;
      dates.forEach(date => {
        total += getGroupTipForDay(groupId, date);
      });
      return total;
    },
    [dates, getGroupTipForDay]
  );

  const getDayTotal = useCallback(
    (date: string): number => {
      let total = 0;
      tableRows.forEach(row => {
        if (row.type === 'user') {
          // User inside a group
          total += calculateUserTip(row.id, date);
        } else if (row.type === 'group' && !row.isRealGroup) {
          // Standalone user (not a real group)
          total += calculateUserTip(row.id, date);
        }
      });
      return total;
    },
    [tableRows, calculateUserTip]
  );

  return {
    calculateUserTip,
    getUserTotalTips,
    getGroupTipForDay,
    getGroupTotalTips,
    getDayTotal,
  };
};
